import { useState, useCallback, useMemo } from "react";

export interface IWidgetFilterItem<T = any> {
    id: string;
    text: string;
    data: T;
}

export interface IMultiFilterState {
    [metric: string]: IWidgetFilterItem<object>[];
}

export interface IServiceFilterState {
    [paramName: string]: IWidgetFilterItem<{ label: string; value: string }>[];
}

export interface IConsumerDataFiltersState {
    marketingScores?: any;
    eventTypes?: any;
    eventTypesMode?: "any" | "all";
    limit?: number;
    offset?: number;
}

export const useWidgetFiltersController = () => {
    const [hasUserInteractedWithFilters, setHasUserInteractedWithFilters] = useState(false);

    // True when a configured campaign/dimension data-filter drives the dimension selector. Lets the
    // table consumers tell "a filter is active but nothing is selected" (show none) apart from
    // "no dimension filter at all" (show everything). Set by DimensionFilter when it seeds.
    const [dimensionFilterActive, setDimensionFilterActive] = useState(false);

    const [selectedDimensionItems, _setSelectedDimensionItems] = useState<IWidgetFilterItem<object>[]>([]);
    const [selectedProducts, _setSelectedProducts] = useState<IWidgetFilterItem<{ productId: number; productName: string }>[]>([]);
    const [selectedPeriodItems, _setSelectedPeriodItems] = useState<IWidgetFilterItem<string>[]>([]);
    const [selectedDateRange, setSelectedDateRange] = useState<{ from: Date; to: Date } | null>(null);
    const [selectedCompareEnabled, setSelectedCompareEnabled] = useState<boolean>(false);
    const [selectedCompareDateRange, setSelectedCompareDateRange] = useState<{ from: Date; to: Date } | null>(null);

    const [selectedFilters, _setSelectedFilters] = useState<IMultiFilterState>({});

    const [selectedServiceFilters, _setSelectedServiceFilters] = useState<IServiceFilterState>({});

    const [consumerDataFilters, _setConsumerDataFilters] = useState<IConsumerDataFiltersState>({});

    const markUserInteracted = useCallback(() => {
        setHasUserInteractedWithFilters(true);
    }, []);

    const setSelectedDimensionItems = useCallback(
        (items: IWidgetFilterItem<object>[], initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setSelectedDimensionItems(items);
        },
        [markUserInteracted],
    );

    const setSelectedProducts = useCallback(
        (items: IWidgetFilterItem<{ productId: number; productName: string }>[], initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setSelectedProducts(items);
        },
        [markUserInteracted],
    );

    const setSelectedPeriodItems = useCallback(
        (items: IWidgetFilterItem<string>[], initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setSelectedPeriodItems(items);
        },
        [markUserInteracted],
    );

    const setSelectedFilters = useCallback(
        (metric: string, items: IWidgetFilterItem<object>[], initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setSelectedFilters((prev) => ({
                ...prev,
                [metric]: items,
            }));
        },
        [markUserInteracted],
    );

    const setSelectedServiceFilters = useCallback(
        (paramName: string, items: IWidgetFilterItem<{ label: string; value: string }>[], initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setSelectedServiceFilters((prev) => ({
                ...prev,
                [paramName]: items,
            }));
        },
        [markUserInteracted],
    );

    const setConsumerDataFilters = useCallback(
        (next: IConsumerDataFiltersState, initial?: boolean) => {
            if (!initial) markUserInteracted();
            _setConsumerDataFilters(next);
        },
        [markUserInteracted],
    );

    const getFilterItems = useCallback(
        (metric: string): IWidgetFilterItem<object>[] => {
            return selectedFilters[metric] || [];
        },
        [selectedFilters],
    );

    const getServiceFilterItems = useCallback(
        (paramName: string): IWidgetFilterItem<{ label: string; value: string }>[] => {
            return selectedServiceFilters[paramName] || [];
        },
        [selectedServiceFilters],
    );

    const clearAllFilters = useCallback(() => {
        setHasUserInteractedWithFilters(false);
        _setSelectedFilters({});
        _setSelectedServiceFilters({});
        _setConsumerDataFilters({});
    }, [markUserInteracted]);

    return {
        selectedDimensionItems,
        setSelectedDimensionItems,
        selectedProducts,
        setSelectedProducts,
        selectedPeriodItems,
        setSelectedPeriodItems,
        // DateRange
        selectedDateRange,
        setSelectedDateRange,
        selectedCompareEnabled,
        setSelectedCompareEnabled,
        selectedCompareDateRange,
        setSelectedCompareDateRange,
        // Multi-filter support
        selectedFilters,
        setSelectedFilters,
        getFilterItems,

        // Service-filters support
        selectedServiceFilters,
        setSelectedServiceFilters,
        getServiceFilterItems,
        clearAllFilters,

        // Consumer-data support
        consumerDataFilters,
        setConsumerDataFilters,

        hasUserInteractedWithFilters,

        // Dimension (campaign) filter activation
        dimensionFilterActive,
        setDimensionFilterActive,
    };
};
